import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Sparkles, Lock, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { useMountEffect } from "@/hooks/useMountEffect";
import { useTranslation } from "@/i18n/translations";
import ImageUploader from "@/components/ImageUploader";

const SOCIAL_FIELDS = [
  { key: "phone", label: "📞 Phone", placeholder: "+33 6 12 34 56 78" },
  { key: "instagram", label: "📷 Instagram", placeholder: "https://instagram.com/your-salon" },
  { key: "facebook", label: "📘 Facebook", placeholder: "https://facebook.com/your-page" },
  { key: "tiktok", label: "📺 TikTok", placeholder: "https://tiktok.com/@your-salon" },
  { key: "whatsapp", label: "💬 WhatsApp", placeholder: "+33 6 12 34 56 78" },
];

const emptySocial = () => ({ phone: "", instagram: "", facebook: "", tiktok: "", whatsapp: "", address: "", email: "" });
const newId = () => Math.random().toString(36).slice(2, 10);
const newItem = () => ({ id: newId(), name: "", price: "", duration: "", description: "" });
const newCategory = () => ({ id: newId(), name: "", description: "", items: [newItem()] });
const newPhoto = () => ({ id: newId(), url: "", alt: "" });

function moveItem(arr, idx, dir) {
  const next = [...arr];
  const j = idx + dir;
  if (j < 0 || j >= next.length) return next;
  [next[idx], next[j]] = [next[j], next[idx]];
  return next;
}

export default function MiniSiteBuilder({ API, subscriptionInfo }) {
  const { t, lang } = useTranslation();
  const isPaid = subscriptionInfo?.plan === "professional" || subscriptionInfo?.plan === "unlimited";

  const [data, setData] = useState(null);
  const [titleText, setTitleText] = useState("");
  const [taglineText, setTaglineText] = useState("");
  const [aboutText, setAboutText] = useState("");
  const [hoursText, setHoursText] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [social, setSocial] = useState(emptySocial());
  const [services, setServices] = useState([]); // [{id,name,description,items:[{id,name,price,duration,description}]}]
  const [photos, setPhotos] = useState([]);     // [{id,url,alt}]
  const [isActive, setIsActive] = useState(true);
  const [currency, setCurrency] = useState("€");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [seoKeywords, setSeoKeywords] = useState("");
  const [accentColor, setAccentColor] = useState("#111111");
  const [team, setTeam] = useState([]); // [{id,name,role,photo_url}]
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [autoCfg, setAutoCfg] = useState(null); // auto services/staff from the Team section

  useEffect(() => {
    if (!data?.salon_id) return;
    axios.get(`${API}/public/booking/${data.salon_id}/config`)
      .then((r) => setAutoCfg(r.data)).catch(() => {});
  }, [data?.salon_id]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/minisite/mine`);
      setData(r.data);
      const ms = r.data?.mini_site || {};
      setTitleText(ms.title || r.data?.salon_name || "");
      setTaglineText(ms.tagline || "");
      setAboutText(ms.about || "");
      setHoursText(ms.hours || "");
      setCoverUrl(r.data?.cover_url || "");
      setLogoUrl(r.data?.logo_url || "");
      setIsActive(ms.is_active !== false); // default true (backward compat)
      setCurrency(ms.currency || "€");
      setSeoTitle(ms.seo_title || "");
      setSeoDescription(ms.seo_description || "");
      setSeoKeywords(ms.seo_keywords || "");
      setAccentColor(/^#[0-9a-fA-F]{6}$/.test(ms.accent_color || "") ? ms.accent_color : "#111111");
      setTeam(
        (ms.team || []).map((m) => ({
          id: m.id || newId(),
          name: m.name || "",
          role: m.role || "",
          photo_url: m.photo_url || "",
        }))
      );
      setSocial({ ...emptySocial(), ...(ms.social || {}) });
      // Normalize services & photos with stable ids for React keys
      setServices(
        (ms.services || []).map((c) => ({
          id: c.id || newId(),
          name: c.name || "",
          description: c.description || "",
          items: (c.items || []).map((it) => ({
            id: it.id || newId(),
            name: it.name || "",
            price: it.price || "",
            duration: it.duration || "",
            description: it.description || "",
          })),
        }))
      );
      setPhotos(
        (ms.photos || []).map((p) => ({
          id: p.id || newId(),
          url: p.url || "",
          alt: p.alt || "",
        }))
      );
    } catch {
      // silent — keep defaults
    }
  };
  useMountEffect(load);

  const save = async () => {
    setSaving(true);
    try {
      const cleanServices = services
        .map((c) => ({
          ...c,
          items: c.items.filter((it) => (it.name || "").trim()),
        }))
        .filter((c) => (c.name || "").trim() || c.items.length);
      const cleanPhotos = photos.filter((p) => (p.url || "").trim());

      await axios.put(`${API}/minisite/mine`, {
        about: aboutText,
        social,
        cover_url: coverUrl,
        logo_url: logoUrl,
        services: cleanServices,
        photos: cleanPhotos,
        is_active: isActive,
        currency,
        title: titleText,
        tagline: taglineText,
        hours: hoursText,
        seo_title: seoTitle,
        seo_description: seoDescription,
        seo_keywords: seoKeywords,
        accent_color: accentColor,
        team: team.filter((m) => (m.name || "").trim()),
      });
      toast.success(t("miniBuilder.toast.saved"));
      load();
    } catch (e) {
      toast.error(t("miniBuilder.toast.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const generateAbout = async () => {
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/minisite/ai-about`, { language: lang });
      setAboutText(r.data?.about || "");
      toast.success(t("miniBuilder.toast.aiSuccess"));
    } catch (e) {
      toast.error(t("miniBuilder.toast.aiFailed"));
    } finally {
      setAiBusy(false);
    }
  };

  // ── Services helpers ────────────────────────────────────────
  const addCategory = () => setServices([...services, newCategory()]);
  const removeCategory = (idx) => setServices(services.filter((_, i) => i !== idx));
  const updateCategory = (idx, patch) =>
    setServices(services.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const moveCategory = (idx, dir) => setServices(moveItem(services, idx, dir));

  const addService = (cIdx) =>
    setServices(services.map((c, i) => (i === cIdx ? { ...c, items: [...c.items, newItem()] } : c)));
  const removeService = (cIdx, sIdx) =>
    setServices(
      services.map((c, i) => (i === cIdx ? { ...c, items: c.items.filter((_, j) => j !== sIdx) } : c))
    );
  const updateService = (cIdx, sIdx, patch) =>
    setServices(
      services.map((c, i) =>
        i === cIdx
          ? { ...c, items: c.items.map((it, j) => (j === sIdx ? { ...it, ...patch } : it)) }
          : c
      )
    );

  // ── Photos helpers ──────────────────────────────────────────
  const addPhoto = () => setPhotos([...photos, newPhoto()]);
  const removePhoto = (idx) => setPhotos(photos.filter((_, i) => i !== idx));
  const updatePhoto = (idx, patch) => setPhotos(photos.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const movePhoto = (idx, dir) => setPhotos(moveItem(photos, idx, dir));

  // ── Team helpers ────────────────────────────────────────────
  const addMember = () => setTeam([...team, { id: newId(), name: "", role: "", photo_url: "" }]);
  const removeMember = (idx) => setTeam(team.filter((_, i) => i !== idx));
  const updateMember = (idx, patch) => setTeam(team.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  const moveMember = (idx, dir) => setTeam(moveItem(team, idx, dir));

  const ACCENT_PRESETS = ["#111111", "#F59E0B", "#EC4899", "#8B5CF6", "#10B981", "#0EA5E9", "#EF4444", "#B45309"];

  if (!isPaid) {
    return (
      <div className="bg-white border-2 md:border-4 border-black rounded-lg p-6 md:p-10 text-center text-black">
        <Lock className="w-12 h-12 mx-auto mb-4" />
        <h2 className="text-2xl md:text-3xl font-bold mb-2">{t("miniBuilder.title")}</h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">{t("miniBuilder.upgradeDesc")}</p>
        <Button
          onClick={() => document.querySelector('[data-testid=subscription-tab]')?.click()}
          className="bg-black text-white hover:bg-gray-800"
          data-testid="minisite-upgrade-btn"
        >
          {t("miniBuilder.upgradeBtn")}
        </Button>
      </div>
    );
  }

  if (!data) {
    return <div className="text-gray-400 p-8 text-center">{t("miniBuilder.loading")}</div>;
  }

  const siteUrl = data.slug ? `https://chatity.io/${data.slug}` : null;
  const inputCls = "border-2 border-black text-gray-900 bg-white placeholder:text-gray-400";

  return (
    <div className="bg-white border-2 md:border-4 border-black rounded-lg p-4 md:p-6 space-y-6 text-black">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">🌐 {t("miniBuilder.title")}</h2>
          {siteUrl && (
            <a href={siteUrl} target="_blank" rel="noopener noreferrer"
               className="text-sm text-gray-600 hover:underline inline-flex items-center gap-1 mt-1"
               data-testid="minisite-public-link">
              {siteUrl} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {!siteUrl && (
            <p className="text-sm text-amber-700 mt-1">⚠️ {t("miniBuilder.noSlug")}</p>
          )}
        </div>
        <Button onClick={save} disabled={saving}
                className="bg-black text-white hover:bg-gray-800" data-testid="minisite-save-btn">
          {saving ? t("miniBuilder.saving") : `💾 ${t("miniBuilder.save")}`}
        </Button>
      </div>

      {/* Activate / Deactivate toggle (placed BEFORE the title so owners can flip the site off in one click). */}
      <div
        className={`flex items-start gap-3 p-4 rounded-lg border-2 ${
          isActive ? "border-green-500 bg-green-50" : "border-amber-500 bg-amber-50"
        }`}
        data-testid="minisite-active-toggle-row"
      >
        <Switch
          checked={isActive}
          onCheckedChange={setIsActive}
          className="mt-0.5 data-[state=checked]:bg-green-600"
          data-testid="minisite-active-toggle"
        />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-black">
            {isActive ? `🟢 ${t("miniBuilder.active.onTitle")}` : `🔴 ${t("miniBuilder.active.offTitle")}`}
          </div>
          <div className="text-xs text-gray-700 mt-0.5">
            {isActive ? t("miniBuilder.active.onHint") : t("miniBuilder.active.offHint")}
          </div>
        </div>
      </div>

      {/* Business name / Title — goes into <title> + Hero */}
      <div>
        <Label htmlFor="minisite-title" className="font-medium text-sm text-gray-700">
          {t("miniBuilder.titleField.label")}
        </Label>
        <Input
          id="minisite-title"
          value={titleText}
          onChange={(e) => setTitleText(e.target.value)}
          placeholder={t("miniBuilder.titleField.placeholder")}
          className={`mt-1 text-base font-semibold ${inputCls}`}
          data-testid="minisite-title-input"
          maxLength={120}
        />
        <p className="text-xs text-gray-600 mt-1">{t("miniBuilder.titleField.hint")}</p>

        <Label htmlFor="minisite-tagline" className="font-medium text-sm text-gray-700 mt-3 block">
          {t("miniBuilder.tagline.label")}
          <span className="ml-1 text-xs font-normal text-gray-500">{taglineText.length}/80</span>
        </Label>
        <Input
          id="minisite-tagline"
          value={taglineText}
          onChange={(e) => setTaglineText(e.target.value.slice(0, 80))}
          placeholder={t("miniBuilder.tagline.placeholder")}
          className={`mt-1 ${inputCls}`}
          data-testid="minisite-tagline-input"
          maxLength={80}
        />
        <p className="text-xs text-gray-600 mt-1">{t("miniBuilder.tagline.hint")}</p>
      </div>

      <Accordion type="multiple" defaultValue={[]} className="space-y-3">

        {/* SEO settings */}
        <AccordionItem value="seo" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            🔍 {t("miniBuilder.seo.title")}
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-4 bg-white">
            <p className="text-xs text-gray-600">💡 {t("miniBuilder.seo.hint")}</p>
            <div>
              <Label htmlFor="seo_title" className="font-medium text-black">{t("miniBuilder.seo.titleLabel")}</Label>
              <Input
                id="seo_title" value={seoTitle} maxLength={60}
                onChange={(e) => setSeoTitle(e.target.value)}
                placeholder={t("miniBuilder.seo.titlePlaceholder")}
                className={inputCls}
                data-testid="minisite-seo-title-input"
              />
              <p className="text-xs text-gray-500 mt-1">{seoTitle.length}/60 · {t("miniBuilder.seo.titleHint")}</p>
            </div>
            <div>
              <Label htmlFor="seo_description" className="font-medium text-black">{t("miniBuilder.seo.descLabel")}</Label>
              <Textarea
                id="seo_description" value={seoDescription} maxLength={160}
                onChange={(e) => setSeoDescription(e.target.value)}
                placeholder={t("miniBuilder.seo.descPlaceholder")}
                className={`min-h-[70px] ${inputCls}`}
                data-testid="minisite-seo-description-input"
              />
              <p className="text-xs text-gray-500 mt-1">{seoDescription.length}/160 · {t("miniBuilder.seo.descHint")}</p>
            </div>
            <div>
              <Label htmlFor="seo_keywords" className="font-medium text-black">{t("miniBuilder.seo.keywordsLabel")}</Label>
              <Input
                id="seo_keywords" value={seoKeywords} maxLength={300}
                onChange={(e) => setSeoKeywords(e.target.value)}
                placeholder={t("miniBuilder.seo.keywordsPlaceholder")}
                className={inputCls}
                data-testid="minisite-seo-keywords-input"
              />
              <p className="text-xs text-gray-500 mt-1">{t("miniBuilder.seo.keywordsHint")}</p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* About with AI */}
        {/* Accent color — customises public mini-site buttons/headings */}
        <AccordionItem value="colors" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            🎨 {t("miniBuilder.colors.title") || "Цвет мини-сайта"}
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-3 bg-white">
            <p className="text-sm text-gray-600">{t("miniBuilder.colors.hint") || "Акцентный цвет кнопок, цен и заголовков на публичной странице."}</p>
            <div className="flex items-center gap-3 flex-wrap">
              {ACCENT_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setAccentColor(c)}
                  className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${accentColor === c ? "border-black ring-2 ring-offset-2 ring-black" : "border-gray-300"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`minisite-accent-preset-${c.slice(1)}`}
                  aria-label={c}
                />
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border-2 border-black p-0.5 bg-white"
                  data-testid="minisite-accent-color-input"
                />
                <span className="text-sm font-mono text-gray-700">{accentColor}</span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span
                className="inline-flex px-5 py-2 rounded-full text-white text-sm font-bold"
                style={{ backgroundColor: accentColor }}
              >
                💬 {t("miniSite.bookNow") || "Записаться"}
              </span>
              <span className="text-xs text-gray-500">← {t("miniBuilder.colors.preview") || "превью кнопки"}</span>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Team / specialists — DIKIDI-style cards on public page */}
        <AccordionItem value="team" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            👩‍🎨 {t("miniBuilder.team.title") || "Специалисты"} <span className="ml-2 text-xs font-normal text-gray-500">{team.length} / 12</span>
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-3 bg-white">
            {(autoCfg?.staff || []).length > 0 ? (
              <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="minisite-team-auto">
                ✨ {t("miniBuilder.autoTeam") || "Мастера подставляются автоматически из раздела «Команда»"} ({autoCfg.staff.length})
                <a href="/dashboard?tab=team" className="ml-2 font-bold underline">{t("miniBuilder.autoOpen") || "Открыть «Команду»"}</a>
              </div>
            ) : (<>
            {team.length === 0 && (
              <p className="text-sm text-gray-500 italic">{t("miniBuilder.team.empty") || "Добавьте мастеров — они появятся карточками на мини-сайте с кнопкой «Записаться»."}</p>
            )}
            {team.map((m, idx) => (
              <div key={m.id} className="border-2 border-gray-300 rounded-lg p-3 flex items-start gap-3 flex-wrap" data-testid={`minisite-team-row-${idx}`}>
                {m.photo_url ? (
                  <img src={m.photo_url} alt={m.name} className="w-14 h-14 rounded-full object-cover border-2 border-black bg-gray-100"
                       onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="w-14 h-14 rounded-full border-2 border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400">
                    <Sparkles className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1 min-w-[220px] space-y-2">
                  <Input value={m.name} onChange={(e) => updateMember(idx, { name: e.target.value })}
                         placeholder={t("miniBuilder.team.name") || "Имя мастера"} className={inputCls}
                         data-testid={`minisite-team-name-${idx}`} />
                  <Input value={m.role} onChange={(e) => updateMember(idx, { role: e.target.value })}
                         placeholder={t("miniBuilder.team.role") || "Специализация (напр. Мастер маникюра)"} className={inputCls}
                         data-testid={`minisite-team-role-${idx}`} />
                  <div className="flex gap-2 items-center">
                    <Input value={m.photo_url} onChange={(e) => updateMember(idx, { photo_url: e.target.value })}
                           placeholder="https://… (фото)" className={`flex-1 ${inputCls}`}
                           data-testid={`minisite-team-photo-${idx}`} />
                    <ImageUploader API={API} compact onUploaded={(url) => updateMember(idx, { photo_url: url })} />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button type="button" onClick={() => moveMember(idx, -1)} className="p-1 text-gray-500 hover:text-black" aria-label="up"><ChevronUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => moveMember(idx, 1)} className="p-1 text-gray-500 hover:text-black" aria-label="down"><ChevronDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => removeMember(idx)} className="p-1 text-red-500 hover:text-red-700" aria-label="remove"
                          data-testid={`minisite-team-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {team.length < 12 && (
              <Button size="sm" variant="outline" onClick={addMember}
                      className="border-2 border-black text-black hover:bg-gray-50"
                      data-testid="minisite-team-add-btn">
                <Plus className="w-4 h-4 mr-1" /> {t("miniBuilder.team.add") || "Добавить мастера"}
              </Button>
            )}
            </>)}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="about" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            📝 {t("miniBuilder.about.title")}
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-3 bg-white">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label htmlFor="about" className="font-medium text-black">{t("miniBuilder.about.label")}</Label>
              <Button
                size="sm" variant="outline"
                onClick={generateAbout} disabled={aiBusy}
                className="border-2 border-purple-500 text-purple-700 hover:bg-purple-50"
                data-testid="minisite-ai-about-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                {aiBusy ? t("miniBuilder.about.generating") : t("miniBuilder.about.aiBtn")}
              </Button>
            </div>
            <Textarea
              id="about" value={aboutText}
              onChange={(e) => setAboutText(e.target.value)}
              placeholder={t("miniBuilder.about.placeholder")}
              className={`min-h-[180px] ${inputCls}`}
              data-testid="minisite-about-input"
            />
            <p className="text-xs text-gray-600">💡 {t("miniBuilder.about.hint")}</p>
          </AccordionContent>
        </AccordionItem>

        {/* Cover photo */}
        <AccordionItem value="cover" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            🖼 {t("miniBuilder.cover.title")}
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-3 bg-white">
            <Label htmlFor="cover_url" className="font-medium text-sm text-gray-700">
              {t("miniBuilder.cover.desc")}
            </Label>
            <div className="flex gap-2 items-start">
              <Input
                id="cover_url"
                type="url"
                value={coverUrl}
                onChange={(e) => setCoverUrl(e.target.value)}
                placeholder="https://i.imgur.com/yourphoto.jpg"
                className={`flex-1 ${inputCls}`}
                data-testid="minisite-cover-url-input"
              />
              <ImageUploader
                API={API}
                compact
                onUploaded={(url) => setCoverUrl(url)}
              />
            </div>
            {coverUrl && (
              <div className="mt-3 relative w-full h-40 rounded-lg overflow-hidden border-2 border-black bg-gray-100">
                <img
                  src={coverUrl}
                  alt="Cover preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                  data-testid="minisite-cover-preview"
                />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Logo / Avatar (round) — shown in Hero */}
        <AccordionItem value="logo" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            🪪 {t("miniBuilder.logo.title")}
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-3 bg-white">
            <Label htmlFor="logo_url" className="font-medium text-sm text-gray-700">
              {t("miniBuilder.logo.desc")}
            </Label>
            <div className="flex gap-2 items-start">
              <Input
                id="logo_url"
                type="url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://i.imgur.com/yourlogo.png"
                className={`flex-1 ${inputCls}`}
                data-testid="minisite-logo-url-input"
              />
              <ImageUploader
                API={API}
                compact
                onUploaded={(url) => setLogoUrl(url)}
              />
            </div>
            {logoUrl && (
              <div className="mt-3 flex items-center gap-3">
                <img
                  src={logoUrl}
                  alt="Logo preview"
                  className="w-20 h-20 rounded-full object-cover border-2 border-black bg-white"
                  onError={(e) => { e.target.style.display = "none"; }}
                  data-testid="minisite-logo-preview"
                />
                <p className="text-xs text-gray-600 flex-1">{t("miniBuilder.logo.hint")}</p>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Social & contacts (with Address) */}
        <AccordionItem value="social" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            🔗 {t("miniBuilder.social.title")}
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-3 bg-white">
            {SOCIAL_FIELDS.map((f) => (
              <div key={f.key}>
                <Label htmlFor={`social-${f.key}`} className="text-sm text-black">{f.label}</Label>
                <Input
                  id={`social-${f.key}`}
                  value={social[f.key]}
                  onChange={(e) => setSocial({ ...social, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className={`mt-1 ${inputCls}`}
                  data-testid={`minisite-social-${f.key}`}
                />
              </div>
            ))}
            <div>
              <Label htmlFor="social-email" className="text-sm text-black">📧 Email</Label>
              <Input
                id="social-email"
                type="email"
                value={social.email || ""}
                onChange={(e) => setSocial({ ...social, email: e.target.value })}
                placeholder="hello@your-salon.com"
                className={`mt-1 ${inputCls}`}
                data-testid="minisite-social-email"
              />
            </div>
            <div>
              <Label htmlFor="social-address" className="text-sm text-black">📍 {t("miniBuilder.address.label")}</Label>
              <Input
                id="social-address"
                value={social.address || ""}
                onChange={(e) => setSocial({ ...social, address: e.target.value })}
                placeholder={t("miniBuilder.address.placeholder")}
                className={`mt-1 ${inputCls}`}
                data-testid="minisite-social-address"
              />
              <p className="text-xs text-gray-600 mt-1">{t("miniBuilder.address.hint")}</p>
            </div>
            <p className="text-xs text-gray-600">{t("miniBuilder.social.hint")}</p>
          </AccordionContent>
        </AccordionItem>

        {/* Working hours */}
        <AccordionItem value="hours" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            🕒 {t("miniBuilder.hours.title")}
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-2 bg-white">
            <Label htmlFor="minisite-hours" className="font-medium text-sm text-gray-700">
              {t("miniBuilder.hours.label")}
            </Label>
            <Textarea
              id="minisite-hours"
              value={hoursText}
              onChange={(e) => setHoursText(e.target.value)}
              placeholder={t("miniBuilder.hours.placeholder")}
              className={`min-h-[120px] font-mono text-sm ${inputCls}`}
              data-testid="minisite-hours-input"
              maxLength={400}
            />
            <p className="text-xs text-gray-600">{t("miniBuilder.hours.hint")}</p>
          </AccordionContent>
        </AccordionItem>

        {/* Services — Phase 2 */}
        <AccordionItem value="services" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            🛍 {t("miniBuilder.services.title")}
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-4 bg-white">
            {/* Currency selector */}
            <div className="flex items-center gap-3">
              <Label htmlFor="minisite-currency" className="text-sm font-medium text-black whitespace-nowrap">
                💱 {t("miniBuilder.services.currencyLabel")}
              </Label>
              <select
                id="minisite-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="border-2 border-black rounded-md px-3 py-1.5 text-sm font-semibold text-gray-900 bg-white"
                data-testid="minisite-currency-select"
              >
                <option value="€">€ EUR</option>
                <option value="$">$ USD</option>
                <option value="₺">₺ TRY</option>
                <option value="£">£ GBP</option>
                <option value="¥">¥ CNY</option>
                <option value="AED">AED</option>
              </select>
            </div>

            {(autoCfg?.services || []).length > 0 ? (
              <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="minisite-services-auto">
                ✨ {t("miniBuilder.autoServices") || "Услуги и цены подставляются автоматически из раздела «Команда»"} ({autoCfg.services.length})
                <a href="/dashboard?tab=team" className="ml-2 font-bold underline">{t("miniBuilder.autoOpen") || "Открыть «Команду»"}</a>
              </div>
            ) : (<>
            {services.length === 0 && (
              <p className="text-sm text-gray-500 italic">{t("miniBuilder.services.empty")}</p>
            )}

            <Accordion type="multiple" defaultValue={[]} className="space-y-3">
              {services.map((cat, cIdx) => (
                <AccordionItem key={cat.id} value={cat.id}
                               className="border-2 border-gray-300 rounded-lg overflow-hidden"
                               data-testid={`minisite-category-${cIdx}`}>
                  <div className="flex items-center bg-gray-50">
                    <AccordionTrigger className="flex-1 px-3 py-2 hover:bg-gray-100 font-semibold text-sm text-black [&>svg]:text-black text-left">
                      <span className="truncate">
                        {cat.name?.trim() || `${t("miniBuilder.services.unnamedCategory")} #${cIdx + 1}`}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          ({(cat.items || []).filter((it) => (it.name || "").trim()).length})
                        </span>
                      </span>
                    </AccordionTrigger>
                    <div className="flex items-center gap-1 px-2">
                      <Button size="icon" variant="outline" disabled={cIdx === 0}
                              onClick={(e) => { e.stopPropagation(); setServices(moveItem(services, cIdx, -1)); }}
                              className="border-gray-400 h-7 w-7" title="↑">
                        <ChevronUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="outline" disabled={cIdx === services.length - 1}
                              onClick={(e) => { e.stopPropagation(); setServices(moveItem(services, cIdx, 1)); }}
                              className="border-gray-400 h-7 w-7" title="↓">
                        <ChevronDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="outline"
                              onClick={(e) => { e.stopPropagation(); removeCategory(cIdx); }}
                              className="border-red-400 text-red-600 hover:bg-red-50 h-7 w-7"
                              data-testid={`minisite-category-remove-${cIdx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <AccordionContent className="p-3 space-y-3 bg-white">
                    <Input
                      value={cat.name}
                      onChange={(e) => updateCategory(cIdx, { name: e.target.value })}
                      placeholder={t("miniBuilder.services.categoryNamePlaceholder")}
                      className={`font-semibold ${inputCls}`}
                      data-testid={`minisite-category-name-${cIdx}`}
                    />
                    <Input
                      value={cat.description}
                      onChange={(e) => updateCategory(cIdx, { description: e.target.value })}
                      placeholder={t("miniBuilder.services.categoryDescPlaceholder")}
                      className={inputCls}
                    />

                    <div className="pl-3 border-l-2 border-gray-300 space-y-2">
                      {cat.items.map((it, sIdx) => (
                        <div key={it.id} className="grid grid-cols-12 gap-2 items-center">
                          <Input
                            value={it.name}
                            onChange={(e) => updateService(cIdx, sIdx, { name: e.target.value })}
                            placeholder={t("miniBuilder.services.serviceNamePlaceholder")}
                            className={`col-span-5 ${inputCls}`}
                            data-testid={`minisite-service-name-${cIdx}-${sIdx}`}
                          />
                          <Input
                            value={it.duration}
                            onChange={(e) => updateService(cIdx, sIdx, { duration: e.target.value })}
                            placeholder={t("miniBuilder.services.durationPlaceholder")}
                            className={`col-span-3 ${inputCls}`}
                          />
                          <div className="col-span-3 relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-700 font-semibold text-sm pointer-events-none select-none">
                              {currency}
                            </span>
                            <Input
                              value={it.price}
                              onChange={(e) => updateService(cIdx, sIdx, { price: e.target.value })}
                              placeholder={t("miniBuilder.services.pricePlaceholder")}
                              className={`pl-7 ${inputCls}`}
                              data-testid={`minisite-service-price-${cIdx}-${sIdx}`}
                            />
                          </div>
                          <Button size="icon" variant="outline"
                                  onClick={() => removeService(cIdx, sIdx)}
                                  className="col-span-1 border-red-400 text-red-600 hover:bg-red-50 h-9 w-9">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button size="sm" variant="outline"
                              onClick={() => addService(cIdx)}
                              className="border-dashed border-2 border-gray-400 text-gray-600 hover:bg-gray-50"
                              data-testid={`minisite-add-service-${cIdx}`}>
                        <Plus className="w-4 h-4 mr-1" /> {t("miniBuilder.services.addService")}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <Button onClick={addCategory} variant="outline"
                    className="w-full border-dashed border-2 border-black text-black hover:bg-gray-50"
                    data-testid="minisite-add-category">
              <Plus className="w-4 h-4 mr-1" /> {t("miniBuilder.services.addCategory")}
            </Button>
            </>)}
          </AccordionContent>
        </AccordionItem>

        {/* Photo gallery — Phase 2 (max 6 photos for clean 3×2 grid) */}
        <AccordionItem value="photos" className="border-2 border-black rounded-lg overflow-hidden">
          <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-base text-black [&>svg]:text-black">
            🖼 {t("miniBuilder.photos.title")} <span className="ml-2 text-xs font-normal text-gray-500">{photos.length} / 6</span>
          </AccordionTrigger>
          <AccordionContent className="p-4 space-y-3 bg-white">
            {/* Drag-n-drop multi-upload zone — hidden when gallery is full */}
            {photos.length < 6 ? (
              <ImageUploader
                API={API}
                multi
                maxFiles={6 - photos.length}
                onUploaded={(url) =>
                  setPhotos((prev) => prev.length < 6 ? [...prev, { id: newId(), url, alt: "" }] : prev)
                }
              />
            ) : (
              <div className="rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="minisite-gallery-full">
                ✋ {t("miniBuilder.photos.full")}
              </div>
            )}

            {photos.length === 0 && (
              <p className="text-sm text-gray-500 italic">{t("miniBuilder.photos.empty")}</p>
            )}

            {photos.map((p, idx) => (
              <div key={p.id} className="grid grid-cols-12 gap-2 items-start" data-testid={`minisite-photo-${idx}`}>
                <div className="col-span-2">
                  {p.url ? (
                    <img src={p.url} alt={p.alt}
                         className="w-full h-16 object-cover rounded border-2 border-black bg-gray-100"
                         onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-16 rounded border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400 text-xs">
                      preview
                    </div>
                  )}
                </div>
                <div className="col-span-8 space-y-1">
                  <div className="flex gap-2 items-start">
                    <Input
                      value={p.url}
                      onChange={(e) => updatePhoto(idx, { url: e.target.value })}
                      placeholder={t("miniBuilder.photos.urlPlaceholder")}
                      className={`flex-1 ${inputCls}`}
                      data-testid={`minisite-photo-url-${idx}`}
                    />
                    <ImageUploader
                      API={API}
                      compact
                      onUploaded={(url) => updatePhoto(idx, { url })}
                    />
                  </div>
                  <Input
                    value={p.alt}
                    onChange={(e) => updatePhoto(idx, { alt: e.target.value })}
                    placeholder={t("miniBuilder.photos.altPlaceholder")}
                    className={`text-sm ${inputCls}`}
                  />
                </div>
                <div className="col-span-2 flex flex-col gap-1">
                  <Button size="icon" variant="outline" disabled={idx === 0}
                          onClick={() => movePhoto(idx, -1)}
                          className="border-gray-400 h-7 w-full" title="↑">
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="outline" disabled={idx === photos.length - 1}
                          onClick={() => movePhoto(idx, 1)}
                          className="border-gray-400 h-7 w-full" title="↓">
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="outline"
                          onClick={() => removePhoto(idx)}
                          className="border-red-400 text-red-600 hover:bg-red-50 h-7 w-full">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            {photos.length < 6 && (
              <Button onClick={addPhoto} variant="outline"
                      className="w-full border-dashed border-2 border-black text-black hover:bg-gray-50"
                      data-testid="minisite-add-photo">
                <Plus className="w-4 h-4 mr-1" /> {t("miniBuilder.photos.add")}
              </Button>
            )}
            <p className="text-xs text-gray-600">{t("miniBuilder.photos.hint")}</p>
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
